import { useId, useState, useRef, useEffect } from "react";
import { Search, X } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/utils/cn";
import { grcCss } from "@/utils/grcCss";
import { useGRCSearch } from "@/services/grc";
import type { GRCSearchResult, AdvancedFilterCriteria } from "@/types/grc";

interface GRCSearchBoxProps {
  onSelect: (result: GRCSearchResult) => void;
  onClear: () => void;
  className?: string;
}

/**
 * Autocomplete search for GRC Dashboard.
 * - Fires when input length >= 4 (mirrors Blazor ReadItems)
 * - Advanced filter flyout for refining by meeting type, planner, advisor, date range
 * - Toggle: show future events only / include past
 */
export function GRCSearchBox({ onSelect, onClear, className }: GRCSearchBoxProps) {
  const inputId = useId();
  const listId = useId();
  const [query, setQuery] = useState("");
  const [showFuture, setShowFuture] = useState(true);
  const [open, setOpen] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [advanced, setAdvanced] = useState<AdvancedFilterCriteria>({});
  const [dateError, setDateError] = useState<string | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const { data: searchData } = useGRCSearch(query, !showFuture);

  // Close dropdown when clicking outside
  useEffect(() => {
    function onPointerDown(e: PointerEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
        setShowAdvanced(false);
      }
    }
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, []);

  let results = searchData?.searchResults ?? [];

  // Apply advanced client-side filters
  if (Object.values(advanced).some(Boolean)) {
    results = results.filter((r) => {
      if (advanced.meetingName && !r.eventName.toLowerCase().includes(advanced.meetingName.toLowerCase())) return false;
      if (advanced.meetingType && r.meetingType !== advanced.meetingType) return false;
      if (advanced.meetingPlannerName && r.meetingPlannerName !== advanced.meetingPlannerName) return false;
      if (advanced.businessAdvisor && r.businessAdvisor !== advanced.businessAdvisor) return false;
      if (advanced.startDate && r.eventEndDate < advanced.startDate) return false;
      if (advanced.endDate && r.eventStartDate > advanced.endDate) return false;
      return true;
    });
  }

  function handleSelect(result: GRCSearchResult) {
    setQuery(result.eventName);
    setOpen(false);
    onSelect(result);
  }

  function handleClear() {
    setQuery("");
    setAdvanced({});
    setDateError(null);
    setOpen(false);
    setShowAdvanced(false);
    onClear();
  }

  function handleToggleTimeframe() {
    setShowFuture((v) => !v);
  }

  function handleClearAdvancedFilters() {
    setAdvanced({});
    setDateError(null);
    setShowAdvanced(false);
  }

  function validateDates(start?: string | null, end?: string | null) {
    if (start && end && start > end) {
      setDateError("End date must be on or after start date.");
      return false;
    }
    setDateError(null);
    return true;
  }

  const hasActiveAdvanced = Object.values(advanced).some(Boolean);

  return (
    <div ref={wrapperRef} className={cn("relative", className)}>
      {/* Main input */}
      <div className="relative">
        <label htmlFor={inputId} className="sr-only">Search GRC events</label>
        <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          id={inputId}
          type="search"
          role="combobox"
          aria-expanded={open && results.length > 0}
          aria-controls={listId}
          aria-autocomplete="list"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(e.target.value.length >= 4);
          }}
          onFocus={() => { if (query.length >= 4) setOpen(true); }}
          placeholder="Search by Event Id or Event Name"
          className="h-9 w-full min-w-[180px] rounded-none border border-slate-300 pl-8 pr-8 text-sm focus:border-slate-500 focus:outline-none"
        />
        {query && (
          <button
            type="button"
            onClick={handleClear}
            aria-label="Clear search"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}

        {/* Dropdown */}
        {open && (
          <div className="absolute left-0 top-full z-50 mt-1 w-full min-w-[300px] rounded-none border border-slate-200 bg-white shadow-lg">
            <ul
              id={listId}
              role="listbox"
              aria-label="Search results"
              className="max-h-[300px] overflow-y-auto"
            >
              {results.length > 0 ? (
                results.map((r) => (
                  <li
                    key={`${r.eventId}-${r.eventName}`}
                    role="option"
                    aria-selected={false}
                    className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50"
                    onClick={() => handleSelect(r)}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") handleSelect(r); }}
                    tabIndex={0}
                  >
                    <span
                      className={cn("inline-block h-2.5 w-2.5 shrink-0", grcCss(r.status) || "bg-slate-300")}
                      aria-label={r.status.replace("event-status-", "")}
                    />
                    <div className="flex flex-col gap-0.5">
                      <span className="font-medium text-slate-900">{r.eventId} - {r.eventName}</span>
                      <span className="text-xs text-slate-500">
                        {`${format(new Date(r.eventStartDate), "MMM dd")}-${format(new Date(r.eventEndDate), "dd, yyyy")}`}
                      </span>
                    </div>
                  </li>
                ))
              ) : (
                <li className="flex h-[300px] items-center justify-center text-sm text-slate-500">
                  No results found
                </li>
              )}
            </ul>

            <div className="border-t border-slate-200 px-2 py-2 text-center">
              <button
                type="button"
                onClick={handleToggleTimeframe}
                className="text-[11px] font-medium text-sky-700 hover:underline"
              >
                View {showFuture ? "Past" : "Current"} Events
              </button>
              <span className="px-2 text-slate-400">|</span>
              <button
                type="button"
                onClick={hasActiveAdvanced ? handleClearAdvancedFilters : () => setShowAdvanced(true)}
                className="text-[11px] font-medium text-sky-700 hover:underline"
              >
                {hasActiveAdvanced ? "Clear Filter" : "Advanced Search"}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Advanced search flyout */}
      {showAdvanced && (
        <div className="absolute left-0 top-full z-[60] mt-1 w-full min-w-[300px] rounded-none border border-slate-200 bg-white p-4 shadow-lg">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-600">
            Advanced Filters
          </p>
          <div className="space-y-3">
            {/* Meeting Name */}
            <div className="border border-[#D0D0CE] px-1.5 pb-1.5">
              <label htmlFor="adv-meeting-name" className="mt-0.5 ml-1.5 block text-[11px] font-semibold text-[#53565A]">
                Meeting Name
              </label>
              <input
                id="adv-meeting-name"
                type="text"
                value={advanced.meetingName ?? ""}
                onChange={(e) => setAdvanced((a) => ({ ...a, meetingName: e.target.value }))}
                className="w-full rounded-none border-0 bg-transparent px-1.5 py-1.5 text-sm focus:outline-none focus:ring-0"
              />
            </div>

            {/* Meeting Type */}
            <div className="border border-[#D0D0CE] px-1.5 pb-1.5">
              <label htmlFor="adv-meeting-type" className="mt-0.5 ml-1.5 block text-[11px] font-semibold text-[#53565A]">
                Meeting Type
              </label>
              <select
                id="adv-meeting-type"
                value={advanced.meetingType ?? ""}
                onChange={(e) => setAdvanced((a) => ({ ...a, meetingType: e.target.value || undefined }))}
                className="w-full rounded-none border-0 bg-transparent px-1.5 py-1.5 text-sm focus:outline-none focus:ring-0"
              >
                <option value="">Select</option>
                {(searchData?.meetingTypes ?? []).map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>

            {/* Meeting Planner Name */}
            <div className="border border-[#D0D0CE] px-1.5 pb-1.5">
              <label htmlFor="adv-meeting-planner" className="mt-0.5 ml-1.5 block text-[11px] font-semibold text-[#53565A]">
                Meeting Planner Name
              </label>
              <select
                id="adv-meeting-planner"
                value={advanced.meetingPlannerName ?? ""}
                onChange={(e) => setAdvanced((a) => ({ ...a, meetingPlannerName: e.target.value || undefined }))}
                className="w-full rounded-none border-0 bg-transparent px-1.5 py-1.5 text-sm focus:outline-none focus:ring-0"
              >
                <option value="">Select</option>
                {(searchData?.meetingPlanners ?? []).map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>

            {/* Business Advisor */}
            <div className="border border-[#D0D0CE] px-1.5 pb-1.5">
              <label htmlFor="adv-business-advisor" className="mt-0.5 ml-1.5 block text-[11px] font-semibold text-[#53565A]">
                Business Advisor
              </label>
              <select
                id="adv-business-advisor"
                value={advanced.businessAdvisor ?? ""}
                onChange={(e) => setAdvanced((a) => ({ ...a, businessAdvisor: e.target.value || undefined }))}
                className="w-full rounded-none border-0 bg-transparent px-1.5 py-1.5 text-sm focus:outline-none focus:ring-0"
              >
                <option value="">Select</option>
                {(searchData?.businessAdvisors ?? []).filter(Boolean).map((b) => (
                  <option key={b} value={b ?? ""}>{b}</option>
                ))}
              </select>
            </div>

            {/* Date range */}
            <div className="grid grid-cols-2 gap-2">
              <div className="border border-[#D0D0CE] px-1.5 pb-1.5">
                <label htmlFor="adv-start-date" className="mt-0.5 ml-1.5 block text-[11px] font-semibold text-[#53565A]">
                  From Date
                </label>
                <input
                  id="adv-start-date"
                  type="date"
                  value={advanced.startDate ?? ""}
                  onChange={(e) => {
                    const val = e.target.value || null;
                    setAdvanced((a) => ({ ...a, startDate: val }));
                    validateDates(val, advanced.endDate);
                  }}
                  className="w-full rounded-none border-0 bg-transparent px-1.5 py-1.5 text-sm focus:outline-none focus:ring-0"
                />
              </div>
              <div className="border border-[#D0D0CE] px-1.5 pb-1.5">
                <label htmlFor="adv-end-date" className="mt-0.5 ml-1.5 block text-[11px] font-semibold text-[#53565A]">
                  To Date
                </label>
                <input
                  id="adv-end-date"
                  type="date"
                  value={advanced.endDate ?? ""}
                  min={advanced.startDate ?? undefined}
                  onChange={(e) => {
                    const val = e.target.value || null;
                    setAdvanced((a) => ({ ...a, endDate: val }));
                    validateDates(advanced.startDate, val);
                  }}
                  className="w-full rounded-none border-0 bg-transparent px-1.5 py-1.5 text-sm focus:outline-none focus:ring-0"
                />
              </div>
            </div>
            {dateError && <p className="text-xs text-red-600">{dateError}</p>}
          </div>
          <div className="mt-4 flex items-center justify-between">
            <button
              type="button"
              onClick={handleClearAdvancedFilters}
              className="text-xs text-slate-500 underline hover:text-slate-900"
            >
              Clear form
            </button>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setShowAdvanced(false)}
                className="rounded-none border border-slate-300 px-3 py-1 text-xs text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => setShowAdvanced(false)}
                disabled={!!dateError}
                className="rounded-none border border-slate-900 bg-slate-900 px-3 py-1 text-xs text-white disabled:opacity-50"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
